import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, GuardResult, RouterStateSnapshot } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { RedirectRequest } from '@azure/msal-browser';
import { LoginService } from '@gea/digital-ui-lib';
import { AuthConfigService, AuthService, OAuthFlow } from '@spartacus/core';
import { CmsPageGuard } from '@spartacus/storefront';
import { EMPTY, Observable } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { msalConfig } from '../../gea/provide/provide-gea';

@Injectable({ providedIn: 'root' })
export class CustomLoginGuard {
  protected authService = inject(AuthService);
  protected authConfigService = inject(AuthConfigService);
  protected cmsPageGuard = inject(CmsPageGuard);
  protected msalService = inject(MsalService);
  protected loginService = inject(LoginService);

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<GuardResult> {
    return this.shouldRenderCMSPage().pipe(
      take(1),
      switchMap((shouldRenderCMSPage) => {
        if (shouldRenderCMSPage) {
          return this.cmsPageGuard.canActivate(route, state);
        }

        // Not logged in → trigger MSAL redirect instead of Spartacus OAuth.
        // Guard against firing while an interaction is already in progress.
        const interactionInProgress =
          this.msalService.instance.getActiveAccount() != null ||
          (this.msalService as any).instance.getAllAccounts().length > 0;

        // If MSAL interaction is already running, don't fire again — just render nothing.
        if (this.isInteractionInProgress()) {
          return EMPTY;
        }

        const redirectRequest: RedirectRequest = {
          authority: msalConfig.auth.authority,
          scopes: ['openid' /*, + your API scope if OCC needs a token */],
        };
        this.loginService.login(redirectRequest);
        return EMPTY; // navigating away — emit nothing, exactly like stock
      }),
    );
  }

  protected shouldRenderCMSPage(): Observable<boolean> {
    return this.authService.isUserLoggedIn().pipe(
      take(1),
      map(
        (isUserLoggedIn) =>
          this.authConfigService.getOAuthFlow() === OAuthFlow.ResourceOwnerPasswordFlow ||
          isUserLoggedIn,
      ),
    );
  }

  protected isInteractionInProgress(): boolean {
    // MSAL tracks this internally; guard against double-firing the redirect.
    return (
      (this.msalService.instance as any).getActiveAccount() == null &&
      // @ts-expect-error internal, or subscribe to inProgress$ instead
      this.msalService.instance['browserStorage']?.getInteractionInProgress?.()
    );
  }
}
